import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Label from '../Elements/Label';
import Select from '../Elements/Select';
import Span from '../Elements/Span';

import { Context } from '../Wizard/WizardWrapper';
import InputFile from '../Elements/InputFile';
import InputText from '../Elements/InputText';
import TextArea from '../Elements/TextArea';
import {
  DECLINE_REASON,
  NOMINATION_STATUS,
  REFEREE_STATUS,
  strapiUrl,
} from '../../../../utils/constants';
import { useLoggedInUserContextProvider } from '../../../../context/LoggedInUserContext';
import DownloadFile from '../Elements/DownloadFile';
import Radio from '../Elements/Radio';
import { useStaticCollections } from '../../../../context/StaticCollections';
import { getReportRoundYear } from '../../../../utils/getReportRoundYear';
import { useAdminActionCollections } from '../../../../context/AdminActionCollections';

const RefereeActions = ({ item }) => {
  const { errors, resetField, formState, trigger, watch, setValue } =
    useContext(Context);
  const { declineReasons } = useStaticCollections();
  const [inputDisabled, setInputDisabled] = useState(false);
  const [edit, setEdit] = useState(false);
  const refereeActions = watch('refereeActions');
  const declineReasonId = watch('declineReasonId');
  const { userDetails } = useLoggedInUserContextProvider();
  const isAdmin =
    userDetails.roles.includes('admin') ||
    userDetails.roles.includes('super-admin');
  const { nomination } = formState || {};
  const canEditNomination =
    nomination?.attributes?.nominationStatusTypeId !==
      NOMINATION_STATUS.ELECTED &&
    nomination?.attributes?.nominationStatusTypeId !==
      NOMINATION_STATUS.EXPIRED &&
    nomination?.attributes?.nominationStatusTypeId !==
      NOMINATION_STATUS.SUSPENDED;
  const canEdit = isAdmin && canEditNomination;
  const [selectedValue, setSelectedValue] = useState(null);
  const { enableRefereeReportUploads } = useAdminActionCollections();

  const location = useLocation();
  const isArchiveView = location.pathname.includes('referee-report-archive');
  const [fileUploading, setFileUploading] = useState(false);

  // Computed values
  const {
    refereeStatusId,
    declineReasonOther,
    extensionReason,
    extensionReasonApprovalNote,
  } = formState.attributes || {};
  const isEditable = inputDisabled === false || edit === true;
  const currentDeclineReasonId =
    declineReasonId || formState.attributes?.declineReasonId;

  const normalizedId =
    typeof currentDeclineReasonId === 'object' &&
    currentDeclineReasonId !== null
      ? currentDeclineReasonId.value || currentDeclineReasonId.id
      : currentDeclineReasonId;

  const showDeclineReasonOther =
    Number(normalizedId) === Number(DECLINE_REASON.OTHER);

  useEffect(() => {
    const fetchData = async () => {
      const shouldDisableInput =
        refereeStatusId !== REFEREE_STATUS.PENDING &&
        refereeStatusId !== REFEREE_STATUS.EXTENSION_APPROVED &&
        refereeStatusId !== REFEREE_STATUS.EXTENSION_REQUESTED;
      setInputDisabled(shouldDisableInput);
    };

    if (!enableRefereeReportUploads) {
      setInputDisabled(true);
    } else {
      fetchData();
    }
  }, [formState, enableRefereeReportUploads, refereeStatusId]);

  useEffect(() => {
    if (inputDisabled && formState?.attributes) {
      const { additionalNotes } = formState.attributes;

      if (extensionReason) setValue('extensionReason', extensionReason);
      if (declineReasonId) setValue('declineReasonId', declineReasonId);
      if (declineReasonOther)
        setValue('declineReasonOther', declineReasonOther);
      if (additionalNotes) setValue('additionalNotes', additionalNotes);
    }
  }, [
    inputDisabled,
    formState?.attributes,
    setValue,
    extensionReason,
    declineReasonId,
    declineReasonOther,
  ]);

  useEffect(() => {
    trigger();
  }, [refereeActions, trigger]);
  useEffect(() => {
    if (selectedValue !== 3) {
      resetField('declineReasonId');
      resetField('declineReasonOther');
    }
    trigger();
  }, [selectedValue, resetField, trigger]);

  const handleEditToggle = () => setEdit((prevEdit) => !prevEdit);

  const setResetValues = (
    currentOptions,
    eventTargetName,
    eventTargetValue,
  ) => {
    const selectedOption = currentOptions.find(
      (option) => option.id === eventTargetValue,
    );

    if (eventTargetName === 'refereeActions') {
      setValue('refereeActions', selectedOption.id);
      [
        'extensionReason',
        'report',
        'reportFileId',
        'reportFileName',
        'reportFilePath',
        'declineReasonId',
        'declineReasonOther',
        'additionalNotes',
      ].forEach(resetField);
    }

    if (eventTargetName === 'declineReasonId') {
      setValue('declineReasonId', selectedOption ? selectedOption.id : null);
      if (
        selectedOption &&
        Number(selectedOption.id) !== Number(DECLINE_REASON.OTHER)
      ) {
        resetField('declineReasonOther');
      }
    }

    trigger();
  };

  const renderFileUploadSection = () => {
    if (selectedValue !== 2 || (inputDisabled && !edit)) return null;

    return (
      <div>
        {isEditable && (
          <InputFile
            name='report'
            fileExtension={[{ id: 1, attributes: { name: 'pdf' } }]}
            fileUploading={fileUploading}
            setFileUploading={setFileUploading}
          />
        )}
        {errors.report && <Span text={errors.report.message} />}
      </div>
    );
  };

  const renderExtensionReasonSection = () => {
    if (selectedValue !== 5 || (inputDisabled && !edit)) return null;

    return (
      <div className='col-lg-6 mt-4'>
        <b>
          <Label
            label='Please provide a reason and indicate when you will be able to submit the report'
            name='extensionReasonLabel'
            required={item.required}
          />
        </b>
        <TextArea name='extensionReason' numberOfWords={50} numberOfRows={4} />
        {errors.extensionReason && (
          <Span text={errors.extensionReason.message} />
        )}
      </div>
    );
  };

  const renderAdditionalNotesSection = () => {
    if (selectedValue !== 4 || (inputDisabled && !edit)) return null;

    return (
      <div className='col-lg-6 mt-4'>
        <b>
          <Label
            label='Additional notes (maximum 100 words)'
            name='additionalNotesLabel'
            required={false}
          />
        </b>
        <TextArea name='additionalNotes' numberOfWords={100} />
        {errors.additionalNotes && (
          <Span text={errors.additionalNotes.message} />
        )}
      </div>
    );
  };
  console.log(showDeclineReasonOther);
  const renderDeclineReasonSection = () => {
    // Only show editable version when in edit mode, not after submission
    if (selectedValue !== 3 || (inputDisabled && !edit)) return null;
    return (
      <div className='col-lg-6 mt-4'>
        <b>
          <Label label='Decline Reason' name='declineReasonId' />
        </b>
        <br />
        <Select
          name='declineReasonId'
          options='referee-report-decline-reasons'
          setState={setResetValues}
        />
        {showDeclineReasonOther && (
          <>
            <b>
              <Label
                label='Please enter your decline reason'
                name='declineReasonOther'
              />
            </b>
            <InputText name='declineReasonOther' />
            {errors.declineReasonOther && (
              <Span text={errors.declineReasonOther.message} />
            )}
          </>
        )}
        {errors.declineReasonId && (
          <Span text={errors.declineReasonId.message} />
        )}
      </div>
    );
  };

  const renderUploadedFileInfo = () => {
    if (formState.attributes?.report?.data) {
      const nominationRoundYear = getReportRoundYear(formState.attributes);
      return (
        <div className='mt-2 mb-2'>
          <h3>Most Recent Upload:</h3>
          <DownloadFile
            text={formState.attributes.report.data.attributes.name}
            url={`${strapiUrl}${formState.attributes.report.data.attributes.url}`}
          />
          <p className='uploaded-file'>
            Uploaded round year : {nominationRoundYear}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderDeclineInfo = () => {
    const { refereeDeclineSubmittedYear } = formState.attributes || {};

    if (inputDisabled === true && refereeStatusId === REFEREE_STATUS.DECLINED) {
      return (
        <div className='mt-4'>
          <h3>Referee Report Request Declined</h3>

          <Label label='Decline Reason : ' name='declineReasonId' />

          <p>
            {declineReasonOther ||
              declineReasons[formState?.attributes?.declineReasonId]}
          </p>

          <Label label='Declined On : ' name='declineReasonId' />

          <p>{refereeDeclineSubmittedYear}</p>
        </div>
      );
    }
    return null;
  };

  const renderExtensionInfo = () => {
    if (
      inputDisabled === true &&
      refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED
    ) {
      return (
        <>
          <b>
            <Label
              label='Referee Report Extension Reason : '
              name='refereeExtensionId'
            />
          </b>
          <p>{extensionReason}</p>
        </>
      );
    }
    return null;
  };

  const renderApprovalNoteInfo = () => {
    if (
      inputDisabled === true &&
      refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED
    ) {
      return (
        <>
          <b>
            <Label
              label='Referee Report Approval Note : '
              name='refereeExtensionApprovalNoteId'
            />
          </b>
          <p>{extensionReasonApprovalNote}</p>
        </>
      );
    }
    return null;
  };

  const renderAdditionalNotesInfo = () => {
    const additionalNotes = formState.attributes?.additionalNotes;

    if (
      inputDisabled === true &&
      refereeStatusId === REFEREE_STATUS.USE_EXISTING &&
      additionalNotes
    ) {
      return (
        <div className='mt-4'>
          <h3>Use Existing Report</h3>

          <Label label='Notes : ' name='additionalNotesDisplay' />

          <p>{additionalNotes}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <React.Fragment key={`referee-field-collection-${item.name}`}>
      <div>
        {renderUploadedFileInfo()}
        <div className='form-group mt-4 d-flex justify-content-between'>
          {inputDisabled && canEdit && !isArchiveView && (
            <button
              onClick={handleEditToggle}
              type='button'
              className={`btn btn-${edit ? 'danger' : 'success'}`}
            >
              <span>
                <i className={`fa fa-${edit ? 'x' : 'pencil'}`} />{' '}
                {edit ? 'Cancel' : 'Edit'}
              </span>
            </button>
          )}
        </div>
        {renderDeclineInfo()}
        {renderExtensionInfo()}
        {renderApprovalNoteInfo()}
        {renderAdditionalNotesInfo()}
        {isEditable && (
          <div className='mt-2'>
            <h3>
              <Label
                label='Please upload a file, decline, use existing report or request extension below'
                name='refereeActions'
                required={item.required}
              />
            </h3>
            <div className='form-group col-lg-12'>
              <Radio
                options='referee-actions'
                name='refereeActions'
                setSelectedValue={setSelectedValue}
              />

              {errors.refereeActions && (
                <Span text={errors.refereeActions.message} />
              )}
            </div>
          </div>
        )}
      </div>
      <div className='form-group mt-4'>
        {renderFileUploadSection()}
        {renderExtensionReasonSection()}
        {renderAdditionalNotesSection()}
        {renderDeclineReasonSection()}
      </div>
    </React.Fragment>
  );
};

RefereeActions.propTypes = {
  item: PropTypes.object,
};

export default RefereeActions;
